import React, { useState, useEffect } from 'react';
import ragService from '../../services/rag.service';
import { configAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import ConfirmDeleteDialog from '../../components/common/ConfirmDeleteDialog';
import TenantPicker from '../../components/common/TenantPicker';

const Collections = () => {
  const { user } = useAuthStore();
  const isSuperadmin = user?.role === 'superadmin';
  const canManage = isSuperadmin;
  
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [vectorDbProvider, setVectorDbProvider] = useState('Loading...');
  const [newCollection, setNewCollection] = useState({
    name: '',
    description: '',
    vectorStore: '',
    tenantId: ''
  });
  const [selectedCollectionTenant, setSelectedCollectionTenant] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deletingCollectionId, setDeletingCollectionId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [renameDialog, setRenameDialog] = useState({ open: false, collection: null, newName: '' });
  const [renaming, setRenaming] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Expandable collection detail
  const [expandedCollection, setExpandedCollection] = useState(null);
  const [collectionDocs, setCollectionDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [deletingDocName, setDeletingDocName] = useState(null);
  const [docDeleteDialog, setDocDeleteDialog] = useState({ open: false, doc: null, collectionId: null });

  const toggleCollection = async (collectionId) => {
    if (expandedCollection === collectionId) {
      setExpandedCollection(null);
      setCollectionDocs([]);
      return;
    }
    setExpandedCollection(collectionId);
    await loadCollectionDocuments(collectionId);
  };

  const loadCollectionDocuments = async (collectionId) => {
    setLoadingDocs(true);
    setCollectionDocs([]);
    try {
      const collection = collections.find(c => c.id === collectionId);
      const collectionInternalName = collection?.internal_name || collection?.collection_name;

      const response = await ragService.listAllDocuments();
      const allDocs = response.documents || [];
      const filtered = collectionInternalName
        ? allDocs.filter(doc => doc.collection_name === collectionInternalName)
        : allDocs;
      setCollectionDocs(filtered);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setCollectionDocs([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const openDocDeleteDialog = (doc, collectionId) => {
    setDocDeleteDialog({ open: true, doc, collectionId });
  };

  const handleDeleteDocument = async () => {
    const { doc, collectionId } = docDeleteDialog;
    if (!doc || !collectionId) return;
    const docName = doc.filename || doc.original_filename;
    setDocDeleteDialog({ open: false, doc: null, collectionId: null });
    setDeletingDocName(docName);
    try {
      await ragService.deleteDocument(collectionId, docName);
      setSuccessMessage(`Document "${docName}" deleted`);
      await loadCollectionDocuments(collectionId);
      await loadCollections();
    } catch (err) {
      console.error('Failed to delete document:', err);
      setError(err.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeletingDocName(null);
    }
  };

  useEffect(() => {
    loadCollections();
  }, [isSuperadmin]);

  const loadCollections = async () => {
    setLoading(true);
    setError('');
    try {
      // Load active config to get vector DB provider
      try {
        const configResponse = await configAPI.getActiveSetup();
        const configData = configResponse?.data || configResponse;
        // Use unified active_config from new config structure
        const activeConfig = configData?.active_config || {};
        const provider = activeConfig?.vector_db_provider || 'Not Configured';
        setVectorDbProvider(provider);
        
        // Update newCollection default vectorStore based on config
        setNewCollection(prev => ({ ...prev, vectorStore: provider.toLowerCase() }));
      } catch (configErr) {
        console.warn('⚠️ Config loading failed (non-critical):', configErr);
        setVectorDbProvider('Unknown');
      }

      const response = await ragService.getCollections();
      console.log('✅ Collections Response:', response);

      setCollections(response.collections || []);
      
      // Also load stats
      try {
        const statsResponse = await ragService.getStats();
        console.log('✅ Stats Response:', statsResponse);
        setStats(statsResponse);
      } catch (statsErr) {
        console.error('⚠️ Stats error (non-critical):', statsErr);
      }
    } catch (err) {
      console.error('❌ Failed to load collections:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      let errorMessage = 'Failed to load collections';
      
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          } else if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
          } else {
            errorMessage = JSON.stringify(err.response.data.detail);
          }
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCollection = async (e) => {
    e.preventDefault();
    
    if (!newCollection.name.trim()) {
      setError('Collection name is required');
      return;
    }

    // Superadmins must select a tenant
    if (isSuperadmin && !newCollection.tenantId) {
      setError('Please select a tenant for this collection');
      return;
    }

    setCreating(true);
    setError('');
    setSuccessMessage('');
    
    try {
      console.log('📤 Creating collection:', newCollection);
      
      const payload = {
        name: newCollection.name.trim(),
        description: newCollection.description.trim(),
        vector_store: newCollection.vectorStore
      };
      
      // Add tenant_id for superadmins
      if (isSuperadmin && newCollection.tenantId) {
        payload.tenant_id = newCollection.tenantId;
      }
      
      const response = await ragService.createCollection(payload);

      console.log('✅ Collection created:', response);
      setSuccessMessage('Collection created successfully');
      setShowCreateModal(false);
      setNewCollection({ name: '', description: '', vectorStore: vectorDbProvider.toLowerCase(), tenantId: '' });
      setSelectedCollectionTenant(null);
      await loadCollections();
    } catch (err) {
      console.error('❌ Failed to create collection:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      let errorMessage = 'Failed to create collection';
      
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          } else if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
          } else {
            errorMessage = JSON.stringify(err.response.data.detail);
          }
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const openDeleteDialog = (collection) => {
    setCollectionToDelete(collection);
    setDeleteDialogOpen(true);
  };

  const openRenameDialog = (collection) => {
    setRenameDialog({ open: true, collection, newName: collection.name || '' });
  };

  const handleRenameCollection = async () => {
    const { collection, newName } = renameDialog;
    const trimmed = (newName || '').trim();
    if (!collection || !trimmed) return;
    if (trimmed === collection.name) {
      // No-op — close dialog without hitting the API
      setRenameDialog({ open: false, collection: null, newName: '' });
      return;
    }
    setRenaming(true);
    setError('');
    setSuccessMessage('');
    try {
      await ragService.updateCollection(collection.id, { name: trimmed });
      setSuccessMessage(`Collection renamed to "${trimmed}"`);
      setRenameDialog({ open: false, collection: null, newName: '' });
      await loadCollections();
    } catch (err) {
      console.error('Error renaming collection:', err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to rename collection');
    } finally {
      setRenaming(false);
    }
  };

  const handleDeleteCollection = async () => {
    const collection = collectionToDelete;
    if (!collection) return;
    const collectionName = collection.name || collection.collection_name;

    setDeletingCollectionId(collection.id);
    setDeleteDialogOpen(false);
    setError('');
    setSuccessMessage('');

    try {
      console.log('🗑️ Deleting collection:', collection);
      
      const response = await ragService.deleteCollection(collection.id);
      
      console.log('✅ Delete response:', response);
      setSuccessMessage(`Collection "${collectionName}" deleted successfully`);
      
      // Refresh collections and stats
      await loadCollections();
    } catch (err) {
      console.error('❌ Error deleting collection:', err);
      let errorMessage = 'Failed to delete collection';
      
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          } else {
            errorMessage = JSON.stringify(err.response.data.detail);
          }
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setDeletingCollectionId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const q = searchTerm.trim().toLowerCase();
  const filteredCollections = q
    ? collections.filter((c) =>
        [c.name, c.collection_name, c.internal_name, c.vector_store, c.embedding_model]
          .some((f) => (f || '').toLowerCase().includes(q))
      )
    : collections;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Collections</h1>
          <p className="mt-1 text-sm text-gray-600">Manage your vector database collections</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadCollections}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Refresh
          </button>
          {canManage && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
            >
              <span>+</span>
              <span>Create Collection</span>
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button 
            onClick={() => setError('')}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{successMessage}</span>
          <button 
            onClick={() => setSuccessMessage('')}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Collections</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_collections || 0}</p>
              </div>
              <div className="text-4xl">📚</div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Documents</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_documents || 0}</p>
              </div>
              <div className="text-4xl">📄</div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Chunks</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_chunks || 0}</p>
              </div>
              <div className="text-4xl">🧩</div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Vector Store</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{vectorDbProvider}</p>
              </div>
              <div className="text-4xl">🗄️</div>
            </div>
          </div>
        </div>
      )}

      {/* Collections List */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">
            All Collections ({filteredCollections.length}{q && collections.length !== filteredCollections.length ? ` of ${collections.length}` : ''})
          </h2>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search collections..."
            className="w-full sm:max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="divide-y divide-gray-200">
          {filteredCollections.length > 0 ? (
            filteredCollections.map((collection, index) => {
              const isExpanded = expandedCollection === collection.id;
              return (
                <div key={collection.id || index}>
                  <div
                    className={`px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer ${isExpanded ? 'bg-indigo-50' : ''}`}
                    onClick={() => toggleCollection(collection.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="text-3xl">📦</div>
                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-gray-900 flex items-center space-x-2">
                            <span>{collection.name || collection.collection_name}</span>
                            <span className={`text-gray-400 text-sm transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                          </h3>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                            <span className="font-medium">{collection.vector_store}</span>
                            <span>•</span>
                            <span>{collection.documents_count || 0} documents</span>
                            <span>•</span>
                            <span>{collection.chunks_count || 0} chunks</span>
                            {collection.embedding_model && (() => {
                              const dims = {'text-embedding-3-large': 3072, 'text-embedding-3-small': 1536, 'all-MiniLM-L6-v2': 384}[collection.embedding_model] ?? null;
                              return (<>
                                <span>•</span>
                                <span title="Embedding model" className="font-mono">{collection.embedding_model}</span>
                                {dims && <span className="text-gray-400">· {dims} dims</span>}
                              </>);
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          Active
                        </span>
                        {canManage && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openRenameDialog(collection); }}
                            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors flex items-center space-x-1"
                            title="Rename display label (vector data unchanged)"
                          >
                            <span>✏️</span>
                            <span>Rename</span>
                          </button>
                        )}
                        {canManage && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openDeleteDialog(collection); }}
                            disabled={deletingCollectionId === collection.id}
                            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                          >
                            {deletingCollectionId === collection.id ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                <span>Deleting...</span>
                              </>
                            ) : (
                              <>
                                <span>🗑️</span>
                                <span>Delete</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Document List */}
                  {isExpanded && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Documents in this collection
                      </h4>
                      {loadingDocs ? (
                        <div className="flex items-center space-x-2 py-4">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                          <span className="text-sm text-gray-500">Loading documents...</span>
                        </div>
                      ) : collectionDocs.length === 0 ? (
                        <p className="text-sm text-gray-400 py-2">No documents in this collection</p>
                      ) : (
                        <div className="space-y-1">
                          {collectionDocs.map((doc, docIdx) => {
                            const docName = doc.original_filename || doc.filename || 'Unknown';
                            const isWeb = doc.source_type === 'web_scrape';
                            const displayName = isWeb ? (doc.page_title || doc.source_url || docName) : docName;
                            const isDeletingThis = deletingDocName === (doc.filename || docName);
                            return (
                              <div
                                key={doc.id || docIdx}
                                className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-gray-200"
                              >
                                <div className="flex items-center space-x-3 flex-1 min-w-0">
                                  <span className="text-lg flex-shrink-0">{isWeb ? '🌐' : '📄'}</span>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-800 truncate" title={displayName}>{displayName}</p>
                                    <div className="flex items-center space-x-3 text-xs text-gray-500">
                                      <span>{isWeb ? 'web scrape' : 'upload'}</span>
                                      {doc.chunk_count != null && (
                                        <React.Fragment><span>•</span><span>{doc.chunk_count} chunks</span></React.Fragment>
                                      )}
                                      {isWeb && doc.source_url && (
                                        <React.Fragment><span>•</span><span className="truncate max-w-xs" title={doc.source_url}>{doc.source_url}</span></React.Fragment>
                                      )}
                                      {!isWeb && doc.file_type && (
                                        <React.Fragment><span>•</span><span>{doc.file_type}</span></React.Fragment>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {canManage && (
                                  <button
                                    onClick={() => openDocDeleteDialog(doc, collection.id)}
                                    disabled={isDeletingThis}
                                    className="ml-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                    title="Delete document"
                                  >
                                    {isDeletingThis ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                                    ) : (
                                      <span>🗑️</span>
                                    )}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="px-6 py-12 text-center">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-gray-500 text-lg">No collections found</p>
              <p className="text-gray-400 text-sm mt-1">Create a collection to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Collection Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setCollectionToDelete(null); }}
        onConfirm={handleDeleteCollection}
        title="Delete Collection"
        entityName={collectionToDelete?.name || collectionToDelete?.collection_name || ''}
        warningText={`This will permanently delete ${collectionToDelete?.documents_count || 0} documents and ${collectionToDelete?.chunks_count || 0} chunks. This action cannot be undone.`}
        confirmButtonText="Delete Collection"
      />

      {/* Delete Document Dialog */}
      <ConfirmDeleteDialog
        open={docDeleteDialog.open}
        onClose={() => setDocDeleteDialog({ open: false, doc: null, collectionId: null })}
        onConfirm={handleDeleteDocument}
        title="Delete Document"
        entityName={docDeleteDialog.doc?.original_filename || docDeleteDialog.doc?.filename || ''}
        warningText="This will permanently delete this document and all its chunks from the collection. This action cannot be undone."
        confirmButtonText="Delete Document"
      />

      {/* Rename Collection Modal — display name only; vector data preserved */}
      {renameDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Rename Collection</h3>
            <p className="text-sm text-gray-500 mb-4">
              Updates the display label only. Vector data, embeddings, and the
              internal key (<code className="text-xs">{renameDialog.collection?.internal_name || '—'}</code>) are unchanged.
            </p>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Display name</span>
              <input
                type="text"
                value={renameDialog.newName}
                onChange={(e) => setRenameDialog({ ...renameDialog, newName: e.target.value })}
                maxLength={100}
                autoFocus
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
                placeholder="e.g. servitiCollection"
              />
            </label>
            <div className="mt-6 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setRenameDialog({ open: false, collection: null, newName: '' })}
                disabled={renaming}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRenameCollection}
                disabled={renaming || !(renameDialog.newName || '').trim()}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {renaming ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Collection Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 sm:mx-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create New Collection</h3>
            </div>
            
            <form onSubmit={handleCreateCollection} className="px-6 py-4 space-y-4">
              {/* Tenant Selection - Superadmin Only */}
              {isSuperadmin && (
                <div>
                  <TenantPicker
                    value={newCollection.tenantId}
                    onChange={(tenantId) => setNewCollection({ ...newCollection, tenantId })}
                  selectedTenant={selectedCollectionTenant}
                  onTenantSelect={setSelectedCollectionTenant}
                  label="Target Tenant"
                  required
                  activeOnly={true}
                  helperText="Search and confirm the exact customer tenant before creating this collection."
                  disabled={creating}
                />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Collection Name *
                </label>
                <input
                  type="text"
                  value={newCollection.name}
                  onChange={(e) => setNewCollection({ ...newCollection, name: e.target.value })}
                  placeholder="e.g., MyDocuments"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                  disabled={creating}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newCollection.description}
                  onChange={(e) => setNewCollection({ ...newCollection, description: e.target.value })}
                  placeholder="Optional description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={creating}
                />
              </div>

              {/* Vector Store is determined by active platform config */}
              <div className="bg-gray-50 px-3 py-2 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Vector Store:</span> {vectorDbProvider}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Collections use the active platform configuration
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewCollection({ name: '', description: '', vectorStore: vectorDbProvider.toLowerCase(), tenantId: '' });
                    setSelectedCollectionTenant(null);
                    setError('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Collection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Collections;
